import type {
  ChecklistTemplateAdmin,
  ConfigurationImpactAssessment,
  ConfigurationImpactCounts,
  InspectionAssignment,
  InspectionCaseSummary,
  InspectorQualification,
  TemplateImportRun,
  UserSummary,
} from './admin-api';

const IMPORT_RUN_STATUS_LABELS: Record<string, string> = {
  RECEIVED: 'Получен',
  VALIDATING: 'Проверяется',
  CORRECTING: 'Исправляется',
  VALID: 'Готов к публикации',
  PUBLISH_BLOCKED: 'Публикация заблокирована',
  PUBLISHED: 'Опубликован',
  ABANDONED: 'Отменён',
  FAILED: 'Ошибка',
};

const QUALIFICATION_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Разрешён',
  INACTIVE: 'Не разрешён',
};

const PROFILE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Действует',
  INACTIVE: 'Не действует',
};

const VERSION_STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Утверждена',
};

const COLUMN_LABELS: Record<string, string> = {
  section_code: 'Код раздела',
  section_title: 'Название раздела',
  item_code: 'Код пункта',
  item_label: 'Название пункта',
  item_ordinal: 'Порядок пункта',
  assessment_token: 'Токен оценки',
};

const BINDING_KIND_LABELS: Record<string, string> = {
  column: 'Колонка',
  fixed: 'Фиксированное значение',
  absent: 'Отсутствует',
  source_order: 'Порядок в книге',
};

const ASSESSMENT_LABELS: Record<string, string> = {
  PASS: 'Соответствует',
  FAIL: 'Не соответствует',
  NOT_APPLICABLE: 'Не применимо',
};

const FORMULA_POLICY_LABELS: Record<string, string> = {
  error: 'Формулы — ошибка',
  literal_formula_text: 'Формулы как текст',
};

export function importRunStatusLabel(status: string): string {
  return IMPORT_RUN_STATUS_LABELS[status] ?? status;
}

export function qualificationStatusLabel(status: string): string {
  return QUALIFICATION_STATUS_LABELS[status] ?? status;
}

export function profileStatusLabel(status: string): string {
  return PROFILE_STATUS_LABELS[status] ?? status;
}

export function versionStatusLabel(status: string): string {
  return VERSION_STATUS_LABELS[status] ?? status;
}

export function columnNameLabel(name: string): string {
  return COLUMN_LABELS[name] ?? name;
}

export function bindingKindLabel(kind: string): string {
  return BINDING_KIND_LABELS[kind] ?? kind;
}

export function assessmentLabel(assessment: string): string {
  return ASSESSMENT_LABELS[assessment] ?? assessment;
}

export function formulaPolicyLabel(policy: string): string {
  return FORMULA_POLICY_LABELS[policy] ?? policy;
}

export function templateBusinessLabel(
  template: Pick<ChecklistTemplateAdmin, 'template_code' | 'name'>,
): string {
  return `${template.template_code} — ${template.name}`;
}

/**
 * Human-facing primary label. Prefer template.name; if a reliably delimited
 * descriptive suffix exists after the last ` | ` (e.g. `PREFIX | Описание`),
 * show the suffix only. Does not invent meaning for ambiguous machine names.
 */
export function templatePrimaryDisplayName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return name;
  }
  const separator = trimmed.lastIndexOf('|');
  if (separator >= 0) {
    const suffix = trimmed.slice(separator + 1).trim();
    if (suffix.length > 0) {
      return suffix;
    }
  }
  return trimmed;
}

export function activeVersionLabel(versionCode: string | null): string {
  return versionCode === null ? 'Активная версия не назначена' : `Активная версия: ${versionCode}`;
}

export type TemplateActivationImpactRow = {
  readonly key: string;
  readonly title: string;
  readonly count: number;
  readonly explanation: string;
  readonly emphasize: boolean;
};

/**
 * Business-facing impact rows for normal Admin apply (MANDATORY_PRESTART).
 * Omits zero counts. Does not expose mode/group enum names.
 */
export function templateActivationImpactRows(
  assessment: ConfigurationImpactAssessment,
): readonly TemplateActivationImpactRow[] {
  const counts: ConfigurationImpactCounts = assessment.counts;
  const actionByGroup = new Map(
    assessment.affected_groups.map((row) => [row.group, row.action_required === true] as const),
  );
  const candidates: readonly {
    readonly key: string;
    readonly group: string;
    readonly count: number;
    readonly title: string;
    readonly explanation: string;
  }[] = [
    {
      key: 'draft',
      group: 'DRAFT',
      count: counts.draft_cases,
      title: 'Черновики подрядчиков',
      explanation: 'Потребуется обновить данные перед отправкой.',
    },
    {
      key: 'ready',
      group: 'READY_NOT_STARTED',
      count: counts.ready_not_started_cases,
      title: 'Подготовлены, но ещё не начаты',
      explanation: 'Будут переподготовлены на актуальную версию до начала осмотра.',
    },
    {
      key: 'preloaded',
      group: 'PRELOADED',
      count: counts.preloaded_cases,
      title: 'Загружены на устройства',
      explanation: 'Потребуется обновление подготовленных данных на устройстве.',
    },
    {
      key: 'in_progress',
      group: 'IN_PROGRESS',
      count: counts.in_progress_cases,
      title: 'Уже начаты',
      explanation: 'Не изменятся.',
    },
    {
      key: 'finalized',
      group: 'FINALIZED',
      count: counts.finalized_cases,
      title: 'Завершены',
      explanation: 'Не изменятся.',
    },
  ];
  return candidates
    .filter((row) => row.count > 0)
    .map((row) => ({
      key: row.key,
      title: row.title,
      count: row.count,
      explanation: row.explanation,
      emphasize: actionByGroup.get(row.group) === true,
    }));
}

/** Intro copy for normal Admin template apply (mandatory pre-start). */
export const TEMPLATE_APPLY_IMPACT_INTRO =
  'Новая версия будет применена ко всем осмотрам, которые ещё фактически не начались.';

export const TEMPLATE_APPLY_SUCCESS_NOTICE =
  'Изменения применены. Новая версия будет использоваться во всех осмотрах, которые ещё не начались.';

export function userBusinessLabel(user: Pick<UserSummary, 'display_name' | 'username'>): string {
  const name = user.display_name.trim().length > 0 ? user.display_name : user.username;
  return `${name} (${user.username})`;
}

export function caseBusinessLabel(row: InspectionCaseSummary): string {
  const vehicle = row.vehicle?.registration_number ?? row.vehicle?.vin_or_alternative_identifier;
  return vehicle === undefined || vehicle === null || vehicle.length === 0
    ? row.case_number
    : `${row.case_number} · ${vehicle}`;
}

export function canPublishImportRun(run: TemplateImportRun): boolean {
  return run.status === 'VALID';
}

/** VALID always; PUBLISH_BLOCKED only when staging already has draftable sections+items. */
export function canOpenImportRunInBuilder(run: TemplateImportRun): boolean {
  if (run.status === 'VALID') return true;
  if (run.status !== 'PUBLISH_BLOCKED') return false;
  const staging = run.staging;
  if (staging === null || typeof staging !== 'object') return false;
  const sections = Array.isArray((staging as { sections?: unknown }).sections)
    ? (staging as { sections: unknown[] }).sections
    : [];
  const items = Array.isArray((staging as { items?: unknown }).items)
    ? (staging as { items: unknown[] }).items
    : [];
  return sections.length >= 1 && items.length >= 1;
}

export function currentChecklistAssignment(
  assignments: readonly InspectionAssignment[],
  checklistInstanceId: string,
): InspectionAssignment | null {
  return (
    assignments.find(
      (row) =>
        row.checklist_instance_id === checklistInstanceId &&
        row.inspection_role_code === 'CHECKLIST_INSPECTOR' &&
        row.unassigned_at === null,
    ) ?? null
  );
}

export function existingQualification(
  items: readonly InspectorQualification[],
  userId: string,
  templateId: string,
): InspectorQualification | null {
  if (!Array.isArray(items)) {
    return null;
  }
  return (
    items.find((row) => row.user_id === userId && row.checklist_template_id === templateId) ?? null
  );
}

export function inspectorCandidates(users: readonly UserSummary[]): UserSummary[] {
  return users.filter((row) => row.status === 'ACTIVE' && row.role_codes.includes('INSPECTOR'));
}

export function assignmentUserCandidates(users: readonly UserSummary[]): UserSummary[] {
  return inspectorCandidates(users);
}

export const VALIDATION_ISSUE_MESSAGES: Record<string, string> = {
  SOURCE_SHEET_MISSING: 'Указанный лист отсутствует в загруженной книге.',
  EMPTY_ITEM_CODE: 'Код пункта пустой.',
  EMPTY_ITEM_LABEL: 'Название пункта пустое.',
  INVALID_ORDINAL: 'Порядок пункта должен быть положительным целым числом.',
  DUPLICATE_SECTION_CODE: 'Коды разделов должны быть уникальными.',
  DUPLICATE_ITEM_CODE: 'Коды пунктов должны быть уникальными.',
  ITEM_SECTION_BINDER: 'Код пункта не согласован с кодом раздела.',
  ZERO_SECTION_WITH_ITEMS: 'При наличии пунктов должен существовать хотя бы один раздел.',
  PRESENTATION_ONLY_FIELD: 'Поле только для отображения недопустимо в этом каталоге.',
  DUPLICATE_FIELD_KEY: 'Ключи полей должны быть уникальными.',
  UNSUPPORTED_ASSESSMENT_TOKEN: 'Токен оценки не сопоставлен с допустимым значением.',
  BLANK_NOT_APPLICABLE: 'Пустая ячейка не означает «не применяется».',
  FORMULA_CELL: 'В ячейке обнаружена формула, которую нельзя выполнить.',
  F3_D8_TOKEN: 'Для семейства F3 значение «+-» должно соответствовать FAIL.',
  F3_D8_EVIDENCE: 'Для семейства F3 значение «+-» требует comment и nonconformity_description.',
  MAPPING_NOT_OBJECT: 'Сопоставление должно быть объектом.',
  MAPPING_UNKNOWN_MEMBER: 'Сопоставление содержит неизвестный член.',
  UNSAFE_MAPPING_LANGUAGE: 'Сопоставление содержит недопустимый исполняемый язык.',
  MAPPING_SCHEMA: 'Схема сопоставления не соответствует утверждённой версии.',
  MAPPING_ROW_ORDER: 'Первая строка данных должна быть ниже строки заголовка.',
  MAPPING_FORMULA_POLICY: 'Политика формул недопустима.',
  MAPPING_INTEGER: 'Номер строки должен быть положительным целым числом.',
  MAPPING_COLUMNS: 'Описание колонок некорректно.',
  MAPPING_BINDING: 'Привязка колонки некорректна.',
  MAPPING_TOKENS: 'Токены оценки заданы некорректно.',
  AMBIGUOUS_ASSESSMENT_TOKEN: 'Один токен оценки сопоставлен нескольким значениям.',
  MAPPING_FAIL_EVIDENCE: 'Правило доказательства FAIL задано некорректно.',
  MAPPING_FIELDS: 'Список полей задан некорректно.',
  MAPPING_FIELD: 'Поле чек-листа задано некорректно.',
  MAPPING_FIELD_KEY: 'У поля не указан ключ.',
  MAPPING_FIELD_LABEL: 'У поля не указана подпись.',
  INVALID_BINDING_KIND: 'Вид привязки поля не поддержан.',
  UNSUPPORTED_VALUE_TYPE: 'Тип значения поля не поддержан.',
  UNSUPPORTED_OWNER: 'Владелец поля не поддержан.',
  UNSUPPORTED_PLACEMENT: 'Размещение поля не поддержано.',
  INVALID_SNAPSHOT_PATH: 'Путь снимка поля задан некорректно.',
  SNAPSHOT_PATH_XOR: 'Путь снимка не согласован с видом привязки.',
  INVALID_FIELD_RULE: 'Декларативные правила поля должны оставаться объектами.',
  CORRECTION_NOT_OBJECT: 'Коррекция должна быть объектом.',
  CORRECTION_UNKNOWN_MEMBER: 'Коррекция содержит неизвестный член.',
  CORRECTION_SCHEMA: 'Схема коррекции не соответствует утверждённой версии.',
  UNSAFE_CORRECTION_LANGUAGE: 'Коррекция содержит недопустимый исполняемый язык.',
  CORRECTION_OVERLAY: 'Наложение коррекции задано некорректно.',
  CORRECTION_CLASSIFICATION: 'Классификация не является принятым членом коррекции.',
  CORRECTION_OVERLAY_MEMBER: 'Наложение коррекции содержит неподдерживаемый член.',
  CORRECTION_FORMULA_POLICY: 'Политика формул в коррекции недопустима.',
  CORRECTION_TOKENS: 'Токены оценки в коррекции заданы некорректно.',
  CORRECTION_COLUMNS: 'Колонки в коррекции заданы некорректно.',
  CORRECTION_ITEM_CODE_REWRITES: 'Переименование кодов пунктов задано некорректно.',
  CORRECTION_FIELDS: 'Поля в коррекции заданы некорректно.',
  CORRECTION_FAIL_EVIDENCE: 'Доказательства FAIL в коррекции заданы некорректно.',
  UNMAPPED_SOURCE_BLOCK: 'Часть строк исходного листа пропущена как несопоставленный блок.',
  GENERATED_SECTION_CODE_COLLISION:
    'Сгенерированный код раздела совпал с уже занятым; добавлен суффикс.',
  GENERATED_ITEM_CODE_COLLISION:
    'Сгенерированный код пункта совпал с уже занятым; добавлен суффикс.',
  NO_IMPORT_ITEMS_DISCOVERED: 'В листе не обнаружены пункты для импорта.',
  AMBIGUOUS_IMPORT_HEADER:
    'Заголовок таблицы неоднозначен: одна колонка подходит к нескольким полям.',
  UNSUPPORTED_MAPPING_SCHEMA: 'Схема сопоставления не поддерживается для этого прогона.',
};

export const UNKNOWN_VALIDATION_ISSUE_MESSAGE = 'Ошибка проверки данных импорта.';

export function validationIssueExplanation(code: string): string {
  return VALIDATION_ISSUE_MESSAGES[code] ?? UNKNOWN_VALIDATION_ISSUE_MESSAGE;
}

export function normalizeLogicalFilename(raw: string): string {
  const basename = raw.replace(/\\/g, '/').split('/').pop() ?? raw;
  if (basename.length === 0 || basename === '.' || basename === '..') {
    return raw;
  }
  for (const character of basename) {
    if (character.charCodeAt(0) > 0xff) {
      return basename;
    }
  }
  const bytes = new Uint8Array(basename.length);
  let hasHighByte = false;
  for (let index = 0; index < basename.length; index += 1) {
    const code = basename.charCodeAt(index);
    bytes[index] = code;
    if (code >= 0x80) {
      hasHighByte = true;
    }
  }
  if (!hasHighByte) {
    return basename;
  }
  const decoded = new TextDecoder('utf-8').decode(bytes);
  if (decoded.includes('\uFFFD')) {
    return basename;
  }
  const encoded = new TextEncoder().encode(decoded);
  if (encoded.length !== bytes.length) {
    return basename;
  }
  for (let index = 0; index < bytes.length; index += 1) {
    if (encoded[index] !== bytes[index]) {
      return basename;
    }
  }
  return decoded;
}

export function issueContext(issue: {
  readonly field_key?: string | null;
  readonly item_code?: string | null;
  readonly source_locator?: Record<string, unknown> | null;
}): string | null {
  const parts: string[] = [];
  if (typeof issue.item_code === 'string' && issue.item_code.length > 0) {
    parts.push(`пункт ${issue.item_code}`);
  }
  if (typeof issue.field_key === 'string' && issue.field_key.length > 0) {
    parts.push(`поле ${issue.field_key}`);
  }
  const locator = issue.source_locator;
  if (locator !== null && locator !== undefined) {
    const sheet = locator.sheet;
    const cell = locator.cell;
    if (typeof sheet === 'string' && sheet.length > 0) {
      parts.push(`лист ${sheet}`);
    }
    if (typeof cell === 'string' && cell.length > 0) {
      parts.push(`ячейка ${cell}`);
    }
  }
  return parts.length === 0 ? null : parts.join(' · ');
}
